import React, { useState, useMemo } from "react";
import * as S from "./ModalContratoStyle";
import { IoClose } from "react-icons/io5";
import {
  FaFileContract,
  FaHistory,
  FaSortAmountDown,
  FaCalendarAlt,
  FaFilter,
  FaFilePdf,
} from "react-icons/fa";
import { formatCurrencyBRL, formatDate } from "../../ASSETS/assets";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function ModalContrato({ data, onClose }) {
  const {
    contract,
    shouldProfit,
    totalWithdrawn,
    profitLeft,
    totalPendingWithdrawal,
    totalAntecipated,
  } = data;

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [statusFilter, setStatusFilter] = useState("2");
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  const maxVal = contract.maxValorization || 0;
  const plusPerc = maxVal - 150;
  const hasPlus = plusPerc >= 1;
  const valPlusMonetario = hasPlus ? (plusPerc / 100) * contract.totalPrice : 0;
  const profitCorrect = shouldProfit + valPlusMonetario - totalWithdrawn;

  const getContractStatusName = (s) => {
    if (s === 1) return "Valorizando";
    if (s === 2) return "Finalizado";
    if (s === 4) return "Pendente";
    return "N/A";
  };

  const getWithdrawStatusName = (s) => {
    if (s === 1) return "Pendente";
    if (s === 2) return "Pago";
    if (s === 3) return "Cancelado";
    if (s === 4) return "Recompra";
    return "N/A";
  };

  const maskDescription = (desc, amount) => {
    if (desc?.toLowerCase().includes("descontar")) {
      return "Saque Extra (Plataforma antiga)";
    }
    return `Saque de ${formatCurrencyBRL(amount)}`;
  };

  const processedWithdraws = useMemo(() => {
    let items = contract.withdraws ? [...contract.withdraws] : [];

    if (statusFilter !== "all") {
      items = items.filter((w) => w.status.toString() === statusFilter);
    }

    items.sort((a, b) => {
      let valA, valB;
      if (sortConfig.key === "date") {
        valA = new Date(a.paymentDate || a.dateCreated).getTime();
        valB = new Date(b.paymentDate || b.dateCreated).getTime();
      } else {
        valA = a.amountWithdrawn;
        valB = b.amountWithdrawn;
      }

      if (sortConfig.direction === "asc") return valA > valB ? 1 : -1;
      return valA < valB ? 1 : -1;
    });

    return items;
  }, [contract.withdraws, sortConfig, statusFilter]);

  const totalPages = Math.ceil(processedWithdraws.length / itemsPerPage);
  const currentWithdraws = processedWithdraws.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
    setCurrentPage(1);
  };

  const exportContractPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    const dateStr = new Date().toLocaleString("pt-BR");

    doc.setFillColor(30, 30, 30);
    doc.rect(40, 30, 762, 65, "F");
    doc.setDrawColor(230, 170, 58);
    doc.setLineWidth(2);
    doc.line(40, 30, 40, 95);

    doc.setTextColor(230, 170, 58);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`DETALHES DO CONTRATO #${contract.id}`, 60, 55);

    doc.setTextColor(200, 200, 200);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`STATUS: ${getContractStatusName(contract.status).toUpperCase()}  |  COMPRA: ${formatDate(contract.dateCreated)}`, 60, 75);
    doc.text(`RELATÓRIO GERADO EM: ${dateStr}`, 580, 55);

    const infoBody = [
      ["Data Compra:", formatDate(contract.dateCreated), "Plus (%):", hasPlus ? `${plusPerc}%` : "Sem Plus"],
      ["Status Contrato:", getContractStatusName(contract.status), "Plus (R$):", hasPlus ? formatCurrencyBRL(valPlusMonetario) : "Sem Plus"],
      ["Golden Box:", contract.goldenBox ? `Sim (#${contract.goldenBox.id})` : "Não", "Royalties Gerados e Creditados:", formatCurrencyBRL(totalWithdrawn)],
      ["Valor do Contrato:", formatCurrencyBRL(contract.totalPrice), "Saque Pendente:", formatCurrencyBRL(totalPendingWithdrawal)],
      ["Projeção Contratual Total (Até 150%):", formatCurrencyBRL(shouldProfit), "Ajuste de Benefício Plus (Crédito Excedente já Creditado):", formatCurrencyBRL(profitLeft)],
      ["Valor Antecipado:", formatCurrencyBRL(totalAntecipated), "Benefício Plus conforme Elegibilidade Contratual:", hasPlus ? formatCurrencyBRL(profitCorrect) : formatCurrencyBRL(profitLeft)]
    ];

    autoTable(doc, {
      startY: 110,
      body: infoBody,
      theme: "plain",
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { fontStyle: "bold", textColor: [100, 100, 100], cellWidth: 160 },
        1: { textColor: [30, 30, 30], fontStyle: "bold", cellWidth: 120 },
        2: { fontStyle: "bold", textColor: [100, 100, 100], cellWidth: 180 },
        3: { textColor: [177, 125, 42], fontStyle: "bold" },
      }
    });

    doc.setTextColor(30, 30, 30);
    doc.setFontSize(12);
    doc.text(`HISTÓRICO DE SAQUES (${statusFilter === 'all' ? 'TODOS' : getWithdrawStatusName(parseInt(statusFilter)).toUpperCase()})`, 40, doc.lastAutoTable.finalY + 35);

    const withdrawRows = processedWithdraws.map(w => [
      `#${w.id}`,
      formatDate(w.paymentDate || w.dateCreated),
      getWithdrawStatusName(w.status),
      maskDescription(w.description, w.amountWithdrawn),
      formatCurrencyBRL(w.amountWithdrawn)
    ]);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 45,
      head: [["ID Saque", "Data", "Status", "Descrição Auditoria", "Valor"]],
      body: withdrawRows,
      theme: "grid",
      styles: { fontSize: 8, halign: "center", valign: "middle" },
      headStyles: { fillColor: [177, 125, 42], textColor: [255, 255, 255], fontStyle: "bold" },
      columnStyles: {
        3: { halign: "left" },
        4: { fontStyle: "bold" }
      }
    });

    doc.save(`detalhes_contrato_${contract.id}.pdf`);
  };

  return (
    <S.Overlay onClick={onClose}>
      <S.Container onClick={(e) => e.stopPropagation()}>
        <S.CloseButton onClick={onClose}>
          <IoClose />
        </S.CloseButton>

        <S.Title style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '95%' }}>
          <span><FaFileContract /> Detalhes do Contrato #{contract.id}</span>
          <button 
            onClick={exportContractPDF}
            style={{ 
              background: '#27ae60', 
              color: '#fff', 
              border: 'none', 
              padding: '8px 15px', 
              borderRadius: '4px', 
              fontSize: '13px', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaFilePdf /> Exportar PDF Completo
          </button>
        </S.Title>

        <S.InfoGrid>
          <div>
            <p>Data Compra:</p>
            <strong>{formatDate(contract.dateCreated)}</strong>
          </div>
          <div>
            <p>Status Contrato:</p>
            <strong>{getContractStatusName(contract.status)}</strong>
          </div>
          <div>
            <p>Golden Box:</p>
            <strong>
              {contract.goldenBox ? `Sim (#${contract.goldenBox.id})` : "Não"}
            </strong>
          </div>
          <div>
            <p>Valor do Contrato:</p>
            <strong>{formatCurrencyBRL(contract.totalPrice)}</strong>
          </div>
          <div>
            <p>Projeção Contratual Total (Até 150%):</p>
            <strong className="highlight">
              {formatCurrencyBRL(shouldProfit)}
            </strong>
          </div>
          <div>
            <p>Valor Antecipado:</p>
            <strong style={{ color: "#3498db" }}>
              {formatCurrencyBRL(totalAntecipated)}
            </strong>
          </div>
          <div>
            <p>Plus (%):</p>
            <strong>{hasPlus ? `${plusPerc}%` : "Sem Plus"}</strong>
          </div>
          <div>
            <p>Plus (R$):</p>
            <strong>
              {hasPlus ? formatCurrencyBRL(valPlusMonetario) : "Sem Plus"}
            </strong>
          </div>
          <div>
            <p>Royalties Gerados e Creditados:</p>
            <strong>{formatCurrencyBRL(totalWithdrawn)}</strong>
          </div>
          <div>
            <p>Saque Pendente:</p>
            <strong style={{ color: "#f39c12" }}>
              {formatCurrencyBRL(totalPendingWithdrawal)}
            </strong>
          </div>
          <div>
            <p>Ajuste de Benefício Plus (Crédito Excedente já Creditado):</p>
            <strong>{formatCurrencyBRL(profitLeft)}</strong>
          </div>
          <div>
            <p>Benefício Plus conforme Elegibilidade Contratual:</p>
            <strong className="highlight">
              {hasPlus
                ? formatCurrencyBRL(profitCorrect)
                : formatCurrencyBRL(profitLeft)}
            </strong>
          </div>
        </S.InfoGrid>

        <S.TableSection>
          <S.TableHeaderActions>
            <h3>
              <FaHistory /> Histórico de Saques
            </h3>
            <S.Controls>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FaFilter style={{ color: "#e6aa3a", fontSize: "12px" }} />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">Todos Status</option>
                  <option value="2">Pagos</option>
                  <option value="1">Pendentes</option>
                  <option value="3">Cancelados</option>
                </select>
              </div>

              <S.SortGroup>
                <button
                  onClick={() => handleSort("date")}
                  className={sortConfig.key === "date" ? "active" : ""}
                >
                  <FaCalendarAlt /> Data{" "}
                  {sortConfig.key === "date" &&
                    (sortConfig.direction === "asc" ? "↑" : "↓")}
                </button>
                <button
                  onClick={() => handleSort("amount")}
                  className={sortConfig.key === "amount" ? "active" : ""}
                >
                  <FaSortAmountDown /> Valor{" "}
                  {sortConfig.key === "amount" &&
                    (sortConfig.direction === "asc" ? "↑" : "↓")}
                </button>
              </S.SortGroup>

              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={5}>5 por pág.</option>
                <option value={10}>10 por pág.</option>
                <option value={20}>20 por pág.</option>
              </select>
            </S.Controls>
          </S.TableHeaderActions>

          <S.Table>
            <thead>
              <tr>
                <th>ID Saque</th>
                <th>Data</th>
                <th>Status</th>
                <th>Descrição Auditoria</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              {currentWithdraws.length > 0 ? (
                currentWithdraws.map((w) => (
                  <tr key={w.id}>
                    <td>#{w.id}</td>
                    <td>{formatDate(w.paymentDate || w.dateCreated)}</td>
                    <td>{getWithdrawStatusName(w.status)}</td>
                    <td>{maskDescription(w.description, w.amountWithdrawn)}</td>
                    <td className="amount">
                      {formatCurrencyBRL(w.amountWithdrawn)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", opacity: 0.5 }}>
                    Nenhum saque encontrado para este filtro.
                  </td>
                </tr>
              )}
            </tbody>
          </S.Table>

          {totalPages > 1 && (
            <S.Pagination>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Anterior
              </button>
              <span>
                Página {currentPage} de {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Próxima
              </button>
            </S.Pagination>
          )}
        </S.TableSection>
      </S.Container>
    </S.Overlay>
  );
}